import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDT, confirmDelete } from "@/lib/api";
import { PageHeader, Sel, Tabs, StatusBadge, VerticalBadge, Empty } from "@/components/common";
import { MeetingDialog, MeetingUpdateDialog } from "@/components/ActionDialogs";

export default function Meetings() {
  const { meta, isAdmin, salesUsers } = useAuth();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("today");
  const [f, setF] = useState({ user_id: "", vertical: "" });
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [upd, setUpd] = useState(null);
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/meetings", { params: { ...p, bucket: tab === "all" ? undefined : tab } }).then((r) => setItems(r.data));
    Promise.all(["today", "upcoming", "completed", "missed"].map((b) => api.get("/meetings", { params: { ...p, bucket: b } })))
      .then((rs) => setCounts({ today: rs[0].data.length, upcoming: rs[1].data.length, completed: rs[2].data.length, missed: rs[3].data.length }));
  }, [tab, f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = sp.get("id");
    if (id) api.get("/meetings").then((r) => { const m = r.data.find((x) => x.id === id); if (m) setUpd(m); });
  }, [sp]);

  return (
    <div>
      <PageHeader title="Meetings" subtitle="Today's, upcoming, completed and missed meetings">
        {isAdmin && <div className="w-44"><Sel testid="meetings-filter-salesperson" value={f.user_id} onChange={(v) => setF({ ...f, user_id: v })} placeholder="All salespeople" options={salesUsers.map((u) => ({ value: u.id, label: u.name }))} /></div>}
        <div className="w-36"><Sel testid="meetings-filter-vertical" value={f.vertical} onChange={(v) => setF({ ...f, vertical: v })} placeholder="All verticals" options={meta.verticals} /></div>
        <button data-testid="schedule-meeting-button" onClick={() => setOpen(true)} className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:opacity-90"><Plus className="h-4 w-4" /> Schedule meeting</button>
      </PageHeader>
      <Tabs testid="meetings-tabs" value={tab} onChange={setTab} tabs={[
        { value: "today", label: `Today (${counts.today ?? 0})` }, { value: "upcoming", label: `Upcoming (${counts.upcoming ?? 0})` },
        { value: "completed", label: `Completed (${counts.completed ?? 0})` }, { value: "missed", label: `Missed (${counts.missed ?? 0})` }, { value: "all", label: "All" }]} />
      <div className="crm-card overflow-x-auto">
        <table className="crm-table" data-testid="meetings-table">
          <thead><tr>{["Date & time", "Client", "Meeting", "Type", "Vertical", "Salesperson", "Status", "Outcome", "Next action", ""].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} data-testid={`meeting-row-${m.id}`}>
                <td className="num text-xs">{fmtDT(m.scheduled_at)}</td>
                <td><Link to={`/leads/${m.lead_id}`} className="font-semibold hover:text-primary">{m.client}</Link></td>
                <td className="max-w-56 truncate">{m.title}</td>
                <td className="text-xs">{m.type}</td>
                <td><VerticalBadge v={m.vertical} /></td>
                <td className="text-xs">{m.user_name}</td>
                <td><StatusBadge s={m.status} /></td>
                <td className="max-w-48 truncate text-xs">{m.outcome || "—"}</td>
                <td className="max-w-40 truncate text-xs">{m.next_action || "—"}</td>
                <td className="space-x-3 whitespace-nowrap"><button data-testid={`meeting-update-${m.id}`} onClick={() => setUpd(m)} className="text-xs font-bold text-primary hover:underline">Update</button>
                  {isAdmin && <button data-testid={`meeting-delete-${m.id}`} onClick={() => confirmDelete(`/meetings/${m.id}`, "meeting", load)} className="text-xs font-bold text-rose-600 hover:underline">Delete</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <Empty text="No meetings in this view" />}
      </div>
      <MeetingDialog open={open} onOpenChange={setOpen} onDone={load} />
      <MeetingUpdateDialog open={!!upd} onOpenChange={(o) => !o && setUpd(null)} meeting={upd} onDone={load} />
    </div>
  );
}
